<script setup>
/**
 * FormControlModulecomponentsCategoryComponent
 * Provides configuration hooks for system-wide content type categories.
 * Toggles a visual state configuration engine if executed inside componentSettings,
 * otherwise seamlessly falls back to a structural One-to-Many form layout element.
 */
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeRelationshipOneToMany from "../form-control-type/FormControlTypeRelationshipOneToMany.vue";
import Button from "../Button.vue";

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const route = proxy.$route;

// --- State ---

const inProgress = ref(false);

// --- Computed ---

const isComponentSettings = computed(() => route.name === 'componentSettings');

const isEnabled = computed(() => props.currentData[props.field.name]);

const btnLabel = computed(() => isEnabled.value ? 'Disable categories' : 'Enable categories');

const btnClass = computed(() => isEnabled.value ? 'btn-danger' : 'btn-success');

const componentId = computed(() => props.currentData[props.field.name]);

const component = computed(() => {
    const items = store.state.components.items || [];
    return items.find(o => o.id === componentId.value);
});

// --- Watchers & Lifecycle ---

/**
 * Reactively recalculates descriptive help descriptions based on state.
 */
watch(isEnabled, (enabled) => {
    if (!props.field) return;

    props.field.helpText = enabled
        ? 'Remove the category field from this content type and delete the related categories content type. <strong>All category data, including the categories themselves and their assignments, will be permanently deleted. This action cannot be undone.</strong>'
        : 'Categories let you group and organise items. They make it easier to manage and filter content in the CMS, and they can also be shown on the website as navigation or filters to help visitors browse listings.';
}, { immediate: true });

onMounted(() => {
    if (isComponentSettings.value && props.field) {
        props.field.label = 'Categories';
    }
});

// --- Methods ---

const enableCategories = async () => {
    inProgress.value = true;
    try {
        const tableName = route.params.component;
        const currentId = store.state.components.componentIds[tableName];

        const newComponentId = await store.dispatch('categories/enableCategories', { componentId: currentId });

        store.commit(`${props.formId}/presetData`, {
            name: props.field.name,
            value: newComponentId
        });
    } catch (error) {
        console.error('Failed to enable content module categories:', error);
    } finally {
        inProgress.value = false;
    }
};

const disableCategories = async () => {
    await store.dispatch('modals/show', {
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            modalBody: `
                <div class="alert alert-danger">
                    <p>The category component its data will be lost.</p>
                    <p class="mb-0">This action cannot be undone.</p>
                </div>
            `,
            confirmStr: component.value?.title || '',
            onConfirm: async () => {
                inProgress.value = true;
                try {
                    const tableName = route.params.component;
                    const currentId = store.state.components.componentIds[tableName];

                    await store.dispatch('categories/disableCategories', { componentId: currentId });

                    store.commit(`${props.formId}/presetData`, {
                        name: props.field.name,
                        value: 0
                    });
                } catch (error) {
                    console.error('Failed to isolate and disable component configuration:', error);
                } finally {
                    inProgress.value = false;
                }
            }
        }
    });
};

const toggleCategories = () => {
    if (isEnabled.value) {
        disableCategories();
    } else {
        enableCategories();
    }
};
</script>

<template>
    <div v-if="isComponentSettings" class="d-flex align-items-center">
        <Button
            @click="toggleCategories"
            :default-class="btnClass"
            :disabled="inProgress"
        >
            {{ btnLabel }}
        </Button>

        <span v-if="inProgress" class="ms-2 ajax-spinner-icon ajax-spinner-icon-sm" />
    </div>
    <FormControlTypeRelationshipOneToMany
        v-else
        :current-data="currentData"
        :field="field"
        :form-id="formId"
    />
</template>